import {buildEditedLocationForCatalog} from '../MyAccounts/keapPartnerCatalogState'
import {isInternationalTaxIdCountry} from '../../../../utils/country-registry'

const isIntlTaxId = acct =>
  isInternationalTaxIdCountry(
    acct?.address?.country ?? acct?.country ?? acct?.countryCode,
  )

/**
 * accountToFormValues
 *
 * Maps the API parent account shape → flat UI values for LocationForm.
 *
 * API shape:  account.company, account.email, account.firstName,
 *             account.lastName, account.address.{street1,city,region,
 *             postalCode,phone}, account.locations[0].name, account.billTo
 *
 * UI shape (LOCATION_FIELDS keys):
 *             businessName, email, firstName, lastName, mobile, telephone,
 *             address, city, state, postCode, locationName,
 *             billTo, salesRep, overrideSST, abn, nzbn, salesforceEmail
 */
export function accountToFormValues(account) {
  if (!account) return {}

  const addr = account.address ?? {}

  return {
    // Billing service shape uses 'company'; Redux cache uses 'companyName'
    businessName: account.company ?? account.companyName ?? '',
    // Billing service uses 'email'; Redux cache uses 'accountEmail'
    email: account.email ?? account.accountEmail ?? '',
    firstName: account.firstName ?? '',
    lastName: account.lastName ?? '',
    // addr.phone is the stored phone — mobile for new accounts, business phone for old ones.
    mobile: addr.phone ?? account.phone ?? account.mobile ?? '',
    // telephone: prefer dedicated businessPhone if the billing service returns it separately;
    // fall back to addr.phone so old accounts (where address.phone IS the business phone)
    // continue to populate the field.
    telephone:
      account.telephone ??
      account.businessPhone ??
      addr.businessPhone ??
      addr.phone ??
      '',
    salesforceEmail: account.salesforceEmail ?? '',
    address: addr.street1 ?? '',
    // Redux cache stores city/stateCode at top level (no nested address object)
    city: addr.city ?? account.city ?? '',
    state: addr.region ?? addr.state ?? account.stateCode ?? '',
    postCode: addr.postalCode ?? account.postalCode ?? '',
    // BUSREQ-14451: international countries show the generic Tax ID field instead of
    // ABN/NZBN. It is stored under the same billing `abn` value (Recurly
    // Account.vatNumber), so route that value to taxId and keep abn blank for them.
    abn: isIntlTaxId(account) ? '' : account.abn ?? '',
    nzbn: account.nzbn ?? '',
    taxId: isIntlTaxId(account) ? account.abn ?? '' : '',
    // account.locationName is the raw value from RECURLY_ACCOUNT.ACCOUNT_LOCATION_NAME
    // (account-service.js getAccountById) — distinct from Company Name. firstLocation.name is
    // NOT a fallback for this — it's actually Company Name under a different key (see
    // account-service.js getRepAccounts `name: child.companyName`), so mixing it in here would
    // silently reintroduce the FULSAS-11828 bug this field exists to fix.
    locationName: account.locationName ?? '',
    billTo: account.billTo ?? (account.parentAccountId ? 'parent' : 'self'),
    salesRepOrPartner: account.salesRep ?? 'no',
    xpCode: account.xpCode ?? '',
    overrideSST: account.overrideSST ?? 'no',
    salesRepUser: account.salesRepUser ?? '',
    partnerUser: account.partnerUser ?? '',
    country: addr.country ?? account.country ?? account.countryCode ?? '',
    // FULSAS-11921: true if Recurly has Internal_Account = "Yes" on this account.
    // Populated by getAccountById (account-service.js) for parent accounts only.
    internalAccount:
      (account?.customFields ?? []).find(f => f?.name === 'Internal_Account')
        ?.value === 'Yes',
  }
}

/**
 * locationToFormValues
 *
 * Maps a child location object (from account.locations[])
 * → flat UI values for LocationForm.
 *
 * Child location shape:
 *   { id, code, name, company, address, phone, email }
 *   where `address` is the raw string "street,city,region,country,postalCode"
 */
export function locationToFormValues(loc) {
  // null → blank form for "Add Location"
  if (!loc) {
    return {
      businessName: '',
      email: '',
      firstName: '',
      lastName: '',
      telephone: '',
      mobile: '',
      salesforceEmail: '',
      address: '',
      city: '',
      state: '',
      country: '',
      postCode: '',
      abn: '',
      nzbn: '',
      taxId: '',
      locationName: '',
      billTo: 'parent',
      salesRepOrPartner: 'no',
      overrideSST: 'no',
      salesRepUser: '',
      partnerUser: '',
    }
  }

  // address can be:
  //   1. an object (billing service GET response normalized shape)
  //   2. a comma-separated string "street,city,region,country,postalCode" (old API)
  //   3. undefined (Redux cache — city/stateCode at top level)
  let street = '',
    city = '',
    region = '',
    postalCode = ''
  if (loc.address && typeof loc.address === 'object') {
    street = loc.address.street1 ?? ''
    city = loc.address.city ?? ''
    region = loc.address.region ?? loc.address.state ?? ''
    postalCode = loc.address.postalCode ?? ''
  } else if (typeof loc.address === 'string') {
    ;[street = '', city = '', region = '', , postalCode = ''] = loc.address
      .split(',')
      .map(s => s.trim())
  } else {
    // Redux cache stores city/stateCode at top level
    city = loc.city ?? ''
    region = loc.stateCode ?? ''
  }

  return {
    businessName: loc.company ?? loc.name ?? '',
    email: loc.email ?? '',
    firstName: loc.firstName ?? '',
    lastName: loc.lastName ?? '',
    // loc.address?.phone is the stored phone — mobile for new accounts, business phone for old ones.
    mobile: loc.address?.phone ?? loc.phone ?? loc.mobile ?? '',
    // telephone: prefer dedicated businessPhone; fall back to address.phone for old accounts.
    telephone:
      loc.telephone ??
      loc.businessPhone ??
      loc.address?.businessPhone ??
      loc.address?.phone ??
      loc.phone ??
      '',
    salesforceEmail: loc.salesforceEmail ?? '',
    address: street,
    city,
    state: region,
    postCode: postalCode,
    abn: isIntlTaxId(loc) ? '' : loc.abn ?? '',
    nzbn: loc.nzbn ?? '',
    taxId: isIntlTaxId(loc) ? loc.abn ?? '' : '',
    // loc.locationName is the raw value from RECURLY_ACCOUNT.ACCOUNT_LOCATION_NAME
    // (account-service.js getAccountById) — distinct from Company Name. loc.name is Company
    // Name under a different key, and the firstName/lastName join was reconstructing the
    // Recurly contact-name split done at creation (resolveRecurlyNames), not the original
    // Location Name — neither is a safe fallback here (see FULSAS-11828).
    locationName: loc.locationName ?? '',
    // Child accounts (with a parent) default to 'parent' billing; standalone default 'self'
    billTo: loc.billTo ?? (loc.parentAccountId ? 'parent' : 'self'),
    salesRepOrPartner: loc.salesRep ?? 'no',
    xpCode: loc.xpCode ?? '',
    overrideSST: loc.overrideSST ?? 'no',
    salesRepUser: '',
    partnerUser: '',
    country: loc.address?.country ?? loc.country ?? '',
  }
}

/**
 * formValuesToAccount
 * Reverse mapper — UI values → API parent account shape for saving.
 */
export function formValuesToAccount(original, formValues) {
  return {
    ...original,
    company: formValues.businessName,
    email: formValues.email,
    firstName: formValues.firstName,
    lastName: formValues.lastName,
    mobile: formValues.mobile,
    billTo: formValues.billTo,
    salesRep: formValues.salesRepOrPartner,
    overrideSST: formValues.overrideSST,
    salesforceEmail: formValues.salesforceEmail,
    abn: formValues.abn,
    nzbn: formValues.nzbn,
    taxId: formValues.taxId,
    address: {
      ...original?.address,
      street1: formValues.address,
      city: formValues.city,
      region: formValues.state,
      postalCode: formValues.postCode,
      phone: formValues.telephone,
    },
    locations:
      original?.locations?.map((loc, idx) =>
        idx === 0 ? {...loc, name: formValues.locationName} : loc,
      ) ?? [],
  }
}

/**
 * formValuesToBillingPayload
 * Maps UI form values → the billing service PUT /v1/accounts/{id} body shape.
 * Preserves original country since the form has no country field.
 */
export function formValuesToBillingPayload(formValues, originalAccount) {
  return {
    firstName: formValues.firstName,
    lastName: formValues.lastName,
    company: formValues.businessName,
    email: formValues.email,
    mobile: formValues.mobile,
    salesforceEmail: formValues.salesforceEmail,
    billTo: formValues.billTo,
    salesRep: formValues.salesRepOrPartner,
    overrideSST: formValues.overrideSST,
    // The billing service client only ever reads data.abn (see intg-billing-client.js) —
    // there's no separate nzbn concept downstream, so NZ edits must merge in here or the
    // NZBN typed on this form silently never reaches Recurly.
    // BUSREQ-14451: international accounts carry the value in taxId (abn/nzbn are blank).
    abn: formValues.taxId || (formValues.abn ?? formValues.nzbn ?? null),
    // parentId: ID of the parent account in the billing service (required for child locations)
    parentId: originalAccount?.parentAccountId ?? null,
    // FULSAS-11921: pass checkbox value so the backend can sync to Recurly.
    // Only included when the field is present in formValues (parent form only).
    ...(formValues.internalAccount !== undefined && {
      internalAccount: formValues.internalAccount,
    }),
    address: {
      street1: formValues.address,
      city: formValues.city,
      state: formValues.state,
      postalCode: formValues.postCode,
      country:
        originalAccount?.address?.country || originalAccount?.country || '',
      // mobile is the primary phone (used for SMS payment links); telephone is a fallback
      phone: formValues.mobile || formValues.telephone,
    },
  }
}

/**
 * formValuesToLocation
 * Reverse mapper — UI values → API child location shape for saving.
 */
export function formValuesToLocation(original, formValues) {
  return {
    ...original,
    name: formValues.locationName,
    company: formValues.businessName,
    email: formValues.email,
    // mobile is the primary phone field (used for SMS payment links); telephone is a fallback
    phone: formValues.mobile || formValues.telephone,
    billTo: formValues.billTo,
    // Rebuild the comma-separated address string the API expects
    address: [
      formValues.address,
      formValues.city,
      formValues.state,
      '', // country — preserved from original if needed
      formValues.postCode,
    ].join(','),
  }
}

/**
 * applyValuesToAccount
 * Folds just-saved UI form values back onto a raw account/location record so
 * purchase-link building (buildPurchaseLinkState) sees what was actually
 * saved instead of the stale record fetched before the edit. Called once per
 * edited location (each multi-location review entry has its own values).
 */
export function applyValuesToAccount(account, values) {
  if (!account || !values) return account
  const edited = buildEditedLocationForCatalog(account, values)
  const company = edited.company
  return {
    ...edited,
    // loc.name is Company Name under a different key (see locationToFormValues)
    // and is what the location dropdown labels read first.
    ...(company && {name: company}),
  }
}

// PartnerOps always types the XP / Affiliate Code fresh on Create Purchase Link: never
// prefill it (or the partner toggle) from the account. The modal's PartnerOps defaults
// then select 'keap-partner' for the empty code.
export function clearPartnerAttribution(account) {
  return {...(account || {}), xpCode: '', salesRep: undefined}
}

// Editing target for the PartnerOps XP / Affiliate Code modal. It carries the
// order context (orderMode / locationsOverride / parentAccountForOrder) so
// Save resumes into the same catalog the click started (e.g. parent-pay).
export function buildXpCodeEditTarget(
  cachedAccount,
  accountId,
  {orderMode, locationsOverride, parentAccountForOrder} = {},
) {
  return {
    account: cachedAccount,
    type: 'child',
    accountId,
    loading: Boolean(accountId),
    fromOrder: true,
    isPre5: !!cachedAccount?.isPre5,
    orderMode,
    locationsOverride,
    parentAccountForOrder,
  }
}
